/**
 * Saved Fit card + "How this fit was built" sheet.
 *
 * Companion to snippets/saved-fit-card.liquid.
 *
 * Reads the logged-in customer's real fit provenance from the Sene Hub, via the
 * Shopify App Proxy path below. Shopify signs the proxied request and appends a
 * logged_in_customer_id the hub can trust, so the browser never names a customer
 * and one customer cannot request another's history.
 *
 * Failure is always silent: no customer, no saved fit, or an unreachable hub all
 * leave the block hidden and the PDP exactly as it was.
 */
(function () {
  'use strict';

  /* App Proxy path on the storefront origin, NOT hub.senestudio.com directly.
     Shopify signs the proxied request; a direct call carries no signature and
     the endpoint rejects it with 403 by design.

     Requires an App Proxy (Dev Dashboard) with prefix "apps", subpath "hub",
     pointing at https://hub.senestudio.com/api/v1/shopify/ .

     The trailing slash is deliberate: Shopify appends whatever follows the
     subpath onto the proxy URL, and the Django route is registered as
     .../saved-fit/ . Without it, Django's APPEND_SLASH answers with a redirect
     instead of the JSON. */
  var ENDPOINT_PATH = '/apps/hub/saved-fit/';
  var REQUEST_TIMEOUT_MS = 6000;

  /* How many of the most recent entries show before the rest collapse behind a
     "View N earlier updates" control. Three keeps the sheet readable for the
     common case while leaving a long history reachable rather than truncated -
     some customers accumulate a lot of adjustments. */
  var VISIBLE_ENTRIES = 3;

  /* Everything below runs against ONE #saved-fit-root node, captured here. That
     node does not survive the life of the page: on a pack PDP, switching pack
     size is a combined-listing change, so variant-picker.js morphs the whole of
     <main> and swaps in fresh server markup - which is the spinner state. The
     old node stays bound to this closure and gets the hub's answer while the
     new one on screen spins forever. Hence start()/boot(): re-run against the
     replacement instead of updating a detached node. A reload always looked
     fine because it re-ran the script. */
  function start() {
  var root = document.getElementById('saved-fit-root');
  if (!root) return;

  var sheet = root.querySelector('[data-sf-sheet]');
  var openBtn = root.querySelector('[data-sf-open]');
  var timeline = root.querySelector('[data-sf-timeline]');
  var summaryEl = root.querySelector('[data-sf-summary]');
  var lastFocus = null;

  function setText(selector, value) {
    var nodes = root.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = value;
  }

  function render(data) {
    if (!data || !data.fit_name || !data.history || !data.history.length) return false;

    setText('[data-sf-name]', data.fit_name);
    summaryEl.textContent = data.summary || '';

    timeline.textContent = '';

    /* The API returns the timeline oldest-first, which is the natural shape for
       the data. It reads better newest-first: what happened to this fit most
       recently is what the customer came to find out. Reversing a copy so the
       response itself is left untouched. */
    var entries = data.history.slice().reverse();

    function appendEntry(entry) {
      var li = document.createElement('li');

      var title = document.createElement('div');
      title.className = 'sf-timeline__title';
      title.textContent = entry.title || '';
      li.appendChild(title);

      if (entry.detail) {
        var detail = document.createElement('div');
        detail.className = 'sf-timeline__detail';
        detail.textContent = entry.detail;
        li.appendChild(detail);
      }
      timeline.appendChild(li);
      return li;
    }

    entries.slice(0, VISIBLE_ENTRIES).forEach(appendEntry);

    var hidden = entries.slice(VISIBLE_ENTRIES);
    if (hidden.length) {
      /* Appended last so it sits below the recent entries, which are the ones
         the customer came for. Clicking replaces it with the older rows. */
      var more = document.createElement('li');
      more.className = 'sf-timeline__more';

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'sf-timeline__more-btn';
      button.textContent =
        'View ' + hidden.length + ' earlier update' + (hidden.length === 1 ? '' : 's');

      button.addEventListener('click', function () {
        more.remove();
        hidden.forEach(appendEntry);
      });

      more.appendChild(button);
      timeline.appendChild(more);
    }

    /* Surfaced for analytics and for QA: which of the nine derivation methods
       produced this fit. */
    if (data.method) root.setAttribute('data-fit-method', data.method);

    root.hidden = false;

    return true;
  }

  /* ------------------------------------------------------------------
     Outcome states
     ------------------------------------------------------------------
     Three outcomes, deliberately not two. "The customer has no saved fit" and
     "we could not reach the hub" arrive at the browser looking identical if you
     only ask whether a card was rendered, and they call for opposite responses:
     the first is a real fact about the customer that we can act on, the second
     is our own failure, and acting on it would tell a customer with a perfectly
     good saved fit that they have none.

     Only a successful response that explicitly says has_fit:false counts as
     NONE. A network error, a timeout, a non-2xx, or a 200 whose body is not the
     shape we expect all fall to UNKNOWN, which leaves the PDP exactly as it was.
     Erring toward UNKNOWN is the safe direction. */
  var STATE_FIT = 'fit';
  var STATE_NONE = 'none';
  var STATE_UNKNOWN = 'unknown';

  var STATE_CLASS = {
    fit: 'sf-has-fit',
    none: 'sf-no-fit',
    unknown: 'sf-hub-unknown'
  };

  function setState(state, firstInCategory) {
    /* Surfaced on the element for QA and analytics: which of the three
       outcomes this pageview actually got. */
    root.setAttribute('data-sf-state', state);
    root.setAttribute('data-sf-first-in-category', firstInCategory ? '1' : '0');

    var drawer = document.getElementById('product-size-drawer-new-ui');
    if (!drawer) return;

    /* The Fit and Tee Length selectors are hidden once a garment has been
       fitted, because the saved fit already carries those choices. Before that
       they are the only way to express a preference, so they stay. Driven by the
       hub's first_in_category rather than inferred here: only the hub knows
       whether a fit exists for THIS product type, and guessing from the method
       string would get it wrong for a customer whose only fits are elsewhere. */
    drawer.classList.toggle('sf-first-in-category', !!firstInCategory);

    /* Switch the Custom Fit tab into its saved-fit layout. The CSS lives in
       product-size-selection-new-ui.liquid: Custom Fit moves to the left of the
       pill, and the quiz prompt and preference selectors are hidden because the
       card now speaks for them. Applied only in the FIT state, so both other
       outcomes keep the existing PDP untouched. */
    for (var key in STATE_CLASS) {
      if (Object.prototype.hasOwnProperty.call(STATE_CLASS, key)) {
        drawer.classList.remove(STATE_CLASS[key]);
      }
    }
    drawer.classList.add(STATE_CLASS[state]);
  }

  function openSheet() {
    lastFocus = document.activeElement;
    sheet.hidden = false;
    openBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    var close = sheet.querySelector('.sf-sheet__close');
    if (close) close.focus();
  }

  function closeSheet() {
    sheet.hidden = true;
    openBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function wire() {
    openBtn.addEventListener('click', openSheet);

    var closers = root.querySelectorAll('[data-sf-close]');
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener('click', closeSheet);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !sheet.hidden) closeSheet();
    });
  }


  /* ------------------------------------------------------------------
     Remembered answer

     The page is built by Shopify before anyone knows whether this customer has
     a fit, so the card can only appear after the hub replies (~400-600ms). For
     that window the shopper saw the un-enhanced drawer - quiz block visible -
     and then watched it rearrange. Every page load, for every customer who has
     a fit. Ray reported it three times on 2026-09-01, which is the right number
     of times for something that reads as broken on a page we ask people to buy
     from.

     So the last answer is remembered per customer AND per product type (a fit
     is per garment: a blazer fit says nothing about this tee) and replayed
     synchronously on the next load, then reconciled with the live answer. The
     cache only ever SHORTCUTS a state the hub already gave us for this exact
     pair; it never invents one.

     Keyed by customer so a shared device cannot show one person's fit to
     another, and versioned so the shape can change without reading stale
     entries. Wrapped because Safari private mode throws on localStorage access
     rather than returning null. ------------------------------------------- */
  var CACHE_PREFIX = 'sf_last_v1:';

  function cacheKey() {
    var id = root.getAttribute('data-customer-id');
    var type = root.getAttribute('data-product-type') || '';
    return id ? CACHE_PREFIX + id + ':' + type : null;
  }

  function readCache() {
    var key = cacheKey();
    if (!key) return null;
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed.has_fit === 'boolean' ? parsed : null;
    } catch (e) { return null; }
  }

  function writeCache(data) {
    var key = cacheKey();
    if (!key) return;
    try {
      /* A quota error must not break the drawer: the cache is an optimisation,
         and the live fetch is still authoritative. */
      window.localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {}
  }

  /* Paints the remembered answer before the request goes out. Returns nothing:
     whatever it shows, fetchLive overwrites with the truth a moment later. */
  function applyCached() {
    var cached = readCache();
    if (!cached || !cached.has_fit) return;
    if (render(cached)) setState(STATE_FIT, cached.first_in_category === true);
  }

  /* Fills in what the hub could not, for fits created before it recorded a
     method.

     Those fits have no `method`, so the hub cannot say which garment they came
     from and falls back to "built from your SmartFit quiz" - true, but it
     ignores every order the customer has placed. hub_2.0#1628 fixes this at the
     source from FinishedMeasurement.product; until that ships, the garment is
     read off the customer's own order history in saved-fit-card.liquid.

     Guarded on an EMPTY method on purpose. The moment the hub starts sending
     one, the hub wins and this stops firing - so the two cannot drift into
     disagreeing, and this becomes dead weight to delete rather than a
     competing source of truth.

     Also guarded on first_in_category being false: true means no fit exists for
     this garment type yet, and naming a previous one there would be wrong. */
  function applyPriorGarment(data) {
    if (!data || data.method) return;
    if (data.first_in_category === true) return;

    var garment = root.getAttribute('data-prior-garment');
    if (garment) {
      data.summary = 'based on your ' + garment + ' fit';
    }
    var continuing = root.getAttribute('data-continuing-name');
    if (continuing) {
      data.fit_name = continuing;
    }
  }

  function fetchLive() {
    var customerId = root.getAttribute('data-customer-id');
    if (!customerId) {
      /* Logged out. Not a hub failure and not a statement that the customer has
         no fit, since we have no customer to ask about. */
      setState(STATE_UNKNOWN, false);
      return;
    }

    /* The customer is identified by Shopify from the session and added to the
       signed query string, so it is deliberately not sent from here. */
    var url =
      ENDPOINT_PATH +
      '?product_id=' + encodeURIComponent(root.getAttribute('data-product-id') || '') +
      '&product_type=' + encodeURIComponent(root.getAttribute('data-product-type') || '');

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS) : null;

    fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        /* A non-2xx is our problem, not a statement about the customer, so it
           must throw rather than resolve to a falsy body that would read as
           "no fit". */
        if (!res.ok) throw new Error('saved-fit: HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || typeof data.has_fit !== 'boolean') {
          /* 200 with an unrecognised body. We cannot tell, so we do not guess. */
          setState(STATE_UNKNOWN, false);
          return;
        }
        if (!data.has_fit) {
          /* Recorded too: a customer whose fit went away should stop being
             shown one on the next load. */
          writeCache({ has_fit: false });
          setState(STATE_NONE, false);
          return;
        }
        /* has_fit true but the payload will not render is still a failure of
           ours, not an answer about the customer. */
        applyPriorGarment(data);
        writeCache(data);
        setState(
          render(data) ? STATE_FIT : STATE_UNKNOWN,
          data.first_in_category === true
        );
      })
      .catch(function (err) {
        /* Network error, timeout, abort, bad JSON: hub unreachable.

           Logged rather than swallowed. A silent catch here also hides genuine
           bugs in render(): a throw inside the .then() lands in this handler and
           is indistinguishable from the hub being down, which cost real time to
           diagnose. An abort is expected and stays quiet; anything else is worth
           seeing in the console. */
        if (!err || err.name !== 'AbortError') {
          try { console.warn('[saved-fit] request or render failed:', err); } catch (e) {}
        }
        setState(STATE_UNKNOWN, false);
      })
      .then(function () { if (timer) clearTimeout(timer); });
  }

  /* ------------------------------------------------------------------
     Preview harness
     ------------------------------------------------------------------
     Renders canned scenarios so every branch of the design can be reviewed
     without needing a customer account that happens to match it. There is no
     account that is simultaneously a suit composition, a remake and a pending
     order regeneration, so without this the sheet's rarer states can only be
     reviewed by guessing.

     Payloads below are shaped exactly as the API builds them in
     sene_hub/shopify/api/views/saved_fit.py: history oldest-first, titles
     already carrying their "Mon YYYY" prefix, copy taken verbatim from
     _METHOD_COPY / _ADJUSTMENT_COPY. Anything that renders differently here
     renders differently in production.

     GATE: this is an allow-list on Shopify.theme.role, not a check for
     "not main". Shopify sets role to 'main' on the published theme and
     'unpublished' / 'development' / 'demo' elsewhere. Written this way, a
     missing or renamed Shopify.theme object disables preview rather than
     enabling it - the failure direction that matters, since the other one puts
     fabricated fit history in front of real customers. */
  var PREVIEW_ROLES = { unpublished: 1, development: 1, demo: 1 };

  var QUIZ_ROW = {
    title: 'Nov 2025 · Created from your SmartFit quiz',
    detail: 'measurements set from your quiz answers'
  };

  var PREVIEW_SCENARIOS = {
    new_fit: {
      has_fit: true,
      fit_name: 'Made for Ray',
      method: 'new_fit',
      first_in_category: true,
      summary: 'first order in this category · drafted fresh from your SmartFit quiz',
      history: [QUIZ_ROW]
    },

    /* Quiz finished, nothing ordered yet. The hub has a BodyMeasurement and no
       FinishedMeasurement, so this card is built from the quiz alone. Most new
       customers see this one, and it is the first thing the quiz gets them. */
    quiz_only: {
      has_fit: true,
      fit_name: 'Made for Ray',
      method: '',
      first_in_category: true,
      summary: 'drafted fresh from your SmartFit quiz',
      history: [QUIZ_ROW]
    },

    /* After a first order placed WITHOUT reusing a saved fit. Same origin story
       as quiz_only, but the fit is now real and style-specific. */
    smart_size: {
      has_fit: true,
      fit_name: 'Made for Ray',
      method: 'smart_size',
      summary: 'built from your SmartFit quiz for this style',
      history: [QUIZ_ROW]
    },

    /* Reuse-only: ordering against an unchanged fit is usage, not a build step,
       so the timeline stays at the quiz row. */
    exact_product_reuse: {
      has_fit: true,
      fit_name: "Ray's Unique Fit",
      method: 'exact_product_reuse',
      summary: 'same product, same fit · reused exactly as last time',
      history: [QUIZ_ROW]
    },

    type_level_saved_fit: {
      has_fit: true,
      fit_name: "Ray's Unique Fit",
      method: 'type_level_saved_fit',
      summary: 'based on your Cumulus Crew T-Shirt fit · new style, same body profile',
      history: [QUIZ_ROW]
    },

    /* Same method, but the log recorded no source garment - the generic
       wording the API falls back to. */
    type_level_saved_fit_generic: {
      has_fit: true,
      fit_name: "Ray's Unique Fit",
      method: 'type_level_saved_fit',
      summary: 'based on your saved fit · new style, same body profile',
      history: [QUIZ_ROW]
    },

    blazer_pants_saved_fit: {
      has_fit: true,
      fit_name: 'Made for Ray',
      method: 'blazer_pants_saved_fit',
      summary: 'first Trouser order · measurements generated from your SmartFit quiz',
      history: [QUIZ_ROW]
    },

    suit_saved_fit: {
      has_fit: true,
      fit_name: "Ray's Unique Fit",
      method: 'suit_saved_fit',
      summary: 'based on your Blazer and Pants fits · carried over from your last suit',
      history: [QUIZ_ROW]
    },

    suit_composition: {
      has_fit: true,
      fit_name: 'Made for Ray',
      method: 'suit_composition',
      summary: 'first suit · built from your Blazer and Pants fits',
      history: [QUIZ_ROW]
    },

    remake: {
      has_fit: true,
      fit_name: "Ray's Unique Fit",
      method: 'remake',
      summary: 'updated with your most recent remake',
      history: [
        QUIZ_ROW,
        {
          title: 'Feb 2026 · Remake on your Cumulus Crew T-Shirt',
          detail: 'adjusted by our fit team and saved into this fit'
        }
      ]
    },

    /* Both alteration types, each naming its garment - the ambiguity Abdullah
       raised on #1613, where an unnamed "Alteration" reads as though the garment
       on screen was the one altered. Both garments are T-Shirts because the
       timeline is now scoped to the viewed product's fit profile pool. */
    alterations: {
      has_fit: true,
      fit_name: "Ray's Unique Fit",
      method: 'type_level_saved_fit',
      summary: 'includes an alteration you had made locally',
      history: [
        QUIZ_ROW,
        {
          title: 'Mar 2026 · Alteration to your Cumulus Crew T-Shirt',
          detail: 'altered by our team and saved into this fit'
        },
        {
          title: 'Jun 2026 · Local alteration to your Cumulus V-Neck T-Shirt',
          detail: 'altered locally and noted on this fit'
        }
      ]
    },

    /* Seven rows against VISIBLE_ENTRIES of 3, so the sheet must collapse four
       behind "View 4 earlier updates". */
    long_history: {
      has_fit: true,
      fit_name: "Ray's Unique Fit",
      method: 'remake',
      summary: 'updated with your most recent remake',
      history: [
        QUIZ_ROW,
        {
          title: 'Dec 2025 · Remake on your Cumulus Crew T-Shirt',
          detail: 'adjusted by our fit team and saved into this fit'
        },
        {
          title: 'Jan 2026 · Alteration to your Cumulus V-Neck T-Shirt',
          detail: 'altered by our team and saved into this fit'
        },
        {
          title: 'Mar 2026 · Local alteration to your Cumulus Crew T-Shirt',
          detail: 'altered locally and noted on this fit'
        },
        {
          title: 'Apr 2026 · Remake on your Cumulus V-Neck T-Shirt',
          detail: 'adjusted by our fit team and saved into this fit'
        },
        {
          title: 'Jun 2026 · Local alteration to your Cumulus Crew T-Shirt',
          detail: 'altered locally and noted on this fit'
        },
        {
          title: 'Aug 2026 · Remake on your Cumulus Crew T-Shirt',
          detail: 'adjusted by our fit team and saved into this fit'
        }
      ]
    },

    /* Fits predating processing_logs carry no method, so the subtitle is read
       off the timeline instead of falling through to generic copy. */
    legacy_no_method: {
      has_fit: true,
      fit_name: "Ray's Unique Fit",
      method: '',
      summary: 'updated with your most recent remake',
      history: [
        QUIZ_ROW,
        {
          title: 'Jul 2026 · Remake on your Cumulus Crew T-Shirt',
          detail: 'adjusted by our fit team and saved into this fit'
        }
      ]
    },

    /* Confirmed: the hub answered, and this customer has no saved fit. A real
       fact we are allowed to act on. */
    no_fit: { has_fit: false },

    /* The hub did not answer. Looks identical to the customer today, but it is
       NOT a statement that they have no fit, so nothing may be built on it. */
    hub_down: { unreachable: true }
  };

  /**
   * The scenario named in ?sf_preview=, or null when preview is unavailable.
   *
   * @returns {string|null}
   */
  function previewName() {
    var theme = window.Shopify && window.Shopify.theme;
    var role = theme && theme.role;
    if (!role || !PREVIEW_ROLES[role]) return null;

    var match = /[?&]sf_preview=([A-Za-z0-9_]+)/.exec(window.location.search);
    return match ? match[1] : null;
  }

  /**
   * Render a canned scenario in place of the live fetch.
   *
   * @returns {boolean} true if preview took over, false to fall through to the
   *   real endpoint.
   */
  function runPreview() {
    var name = previewName();
    if (!name) return false;

    var scenario = PREVIEW_SCENARIOS[name];
    if (!scenario) {
      /* An unknown name is a typo in the URL, not a scenario. Say so in the
         console rather than silently falling back to live data, which would
         look like a broken scenario. */
      if (window.console && console.warn) {
        console.warn(
          '[saved-fit] unknown preview scenario "' + name + '". Available: ' +
            Object.keys(PREVIEW_SCENARIOS).join(', ')
        );
      }
      return true;
    }

    if (scenario.unreachable) {
      setState(STATE_UNKNOWN, false);
      return true;
    }
    if (!scenario.has_fit) {
      setState(STATE_NONE, false);
      return true;
    }
    setState(
      render(scenario) ? STATE_FIT : STATE_UNKNOWN,
      scenario.first_in_category === true
    );
    return true;
  }

  wire();
  /* Order matters: the remembered answer paints first so the drawer is already
     correct in the first frame, then the live request confirms or corrects it.
     The preview harness owns the state outright when active, so it skips both. */
  if (!runPreview()) {
    applyCached();
    fetchLive();
  }
  }

  var boundRoot = null;

  /* Re-binds only when the element on screen is genuinely a different node.
     Guarding on identity rather than just re-running keeps a plain colour
     change - which does NOT replace the card - from wiring a second set of
     listeners onto the same buttons. */
  function boot() {
    var el = document.getElementById('saved-fit-root');
    if (!el || el === boundRoot) return;
    boundRoot = el;
    start();
  }

  boot();

  /* variant:update covers a normal variant change; combined-listing:product-changed
     is the one that matters here, dispatched right after <main> is morphed. */
  document.addEventListener('variant:update', boot);
  document.addEventListener('combined-listing:product-changed', boot);
})();
